import { Group, Rect, Text as KText, Shape } from "react-konva";
import KonvaLib from "konva";
import type { FontPair, TextElement as TextEl } from "../types";
import { autoFitTextToBox } from "../utils/smartPlaceholders";
import { convertGradientToKonva } from "./utils/gradientConverter";
import { drawWarpedText } from "./utils/warpedTextRenderer";
import type { WarpType } from "./utils/warpedTextRenderer";
import type { KonvaNodeCommonProps } from "./CanvasStageTypes";
import { parseRgbLike, relativeLuminance, rgbaFromRgb } from "./CanvasStageElementUtils";
import {
  getTextAnimationPreset,
  splitTextByMode,
  getAnimationOrder,
  type TextAnimationDirection,
} from "../presets/textAnimationPresets";

// ============ TEXT ANIMATION HELPERS ============

/**
 * Compute visible text based on textReveal progress (0-1)
 * For simple presets (opacity-only), we show/hide complete units.
 */
function computeVisibleText(
  fullText: string,
  textReveal: number,
  presetId: string | undefined,
  direction: TextAnimationDirection = "forward"
): string {
  if (!presetId || textReveal >= 1) return fullText;
  if (textReveal <= 0) return "";

  const preset = getTextAnimationPreset(presetId);
  if (!preset) return fullText;

  const units = splitTextByMode(fullText, preset.splitMode);
  if (units.length === 0) return "";

  const order = getAnimationOrder(units.length, direction);

  // Calculate how many units are fully visible
  const visibleCount = Math.ceil(textReveal * units.length);

  // Build visible text maintaining original positions
  const visibleIndices = new Set(order.slice(0, visibleCount));

  if (preset.splitMode === "character") {
    return units
      .map((char, i) => (visibleIndices.has(i) ? char : " "))
      .join("")
      .replace(/\s+$/, ""); // Trim trailing spaces
  }

  if (preset.splitMode === "word") {
    return units.filter((_, i) => visibleIndices.has(i)).join(" ");
  }

  // line mode
  return units.filter((_, i) => visibleIndices.has(i)).join("\n");
}

export type RenderTextElementArgs = {
  el: TextEl;
  common: KonvaNodeCommonProps;
  fontPair?: FontPair;
  editingTextId?: string | null;
};

export function renderTextElement({
  el,
  common,
  fontPair,
  editingTextId,
}: RenderTextElementArgs) {
  const t = el;

  // Build font properties
  const fallbackFamily =
    (fontPair?.fallback as string | undefined) ||
    "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial";
  const templateSlot = t.templateFontSlot === "secondary" ? "secondary" : "primary";
  const templateFamily = (fontPair as any)?.[templateSlot] || fallbackFamily;
  const fontFamily = t.useTemplateFont ? templateFamily : (t.fontFamily || fallbackFamily);
  const isBold =
    typeof t.fontWeight === "number"
      ? t.fontWeight >= 600
      : t.fontWeight === "bold";
  const isItalic = t.fontStyle === "italic";
  const fontStyle =
    isBold && isItalic ? "bold italic" : isBold ? "bold" : isItalic ? "italic" : "normal";

  // Build shadow properties — glow takes priority over textShadow when enabled
  const glowEnabled = !!t.textGlow?.enabled;
  const shadowEnabled = glowEnabled ? true : !!t.textShadow;
  const shadowBlur = glowEnabled
    ? (t.textGlow?.radius ?? 20)
    : shadowEnabled ? (t.textShadow?.blur || 0) : 0;
  const shadowOffsetX = glowEnabled ? 0 : (shadowEnabled ? (t.textShadow?.offsetX || 0) : 0);
  const shadowOffsetY = glowEnabled ? 0 : (shadowEnabled ? (t.textShadow?.offsetY || 0) : 0);
  const shadowColor = glowEnabled
    ? (t.textGlow?.color || "rgba(255,255,255,0.8)")
    : (shadowEnabled ? (t.textShadow?.color || "rgba(0,0,0,0.5)") : "");
  const shadowOpacity = glowEnabled ? (t.textGlow?.opacity ?? 0.8) : undefined;

  // Build stroke properties
  const strokeEnabled = !!t.textStroke;
  const strokeWidth = strokeEnabled ? (t.textStroke?.width || 0) : 0;
  const strokeColor = strokeEnabled ? (t.textStroke?.color || "#000000") : "";

  // Build fill properties (gradient or solid)
  const fillProps = t.fillGradient
    ? (() => {
        try {
          const gradient: any = t.fillGradient;
          const stops = Array.isArray(gradient?.stops) ? gradient.stops : [];
          // Konva expects a valid colorStops array; fall back to solid fill if incomplete.
          if (stops.length < 2) return { fill: t.fill ?? "#ffffff" };
          return convertGradientToKonva(
            gradient,
            Math.max(10, t.width),
            Math.max(10, t.height),
            t.rotation || 0
          );
        } catch {
          return { fill: t.fill ?? "#ffffff" };
        }
      })()
    : { fill: t.fill ?? "#ffffff" };

  const boxW = Math.max(10, Number(t.width) || 10);
  const boxH = Math.max(10, Number(t.height) || 10);
  const textPadding = Math.max(0, Number((t as any)?.textBox?.padding ?? 0) || 0);
  const verticalAlignRaw = String((t as any)?.textBox?.verticalAlign ?? "center");
  const verticalAlign =
    verticalAlignRaw === "top" ? "top" : verticalAlignRaw === "bottom" ? "bottom" : "middle";

  const autoFit = (t as any)?.autoFit as any | undefined;
  const mode: string = autoFit?.mode || (autoFit?.enabled ? "shrink-to-fit" : "none");
  const overflow: string = autoFit?.overflow || "clip";
  const baseFontSize = Math.max(1, Number(t.fontSize) || 16);
  const lineHeight = Math.max(0.8, Number(t.lineHeight) || 1.2);

  const availHForLines = Math.max(1, boxH - textPadding * 2);
  const derivedMaxLinesByHeight = Math.max(1, Math.floor(availHForLines / (baseFontSize * lineHeight)));

  const maxLinesForEllipsis = Number.isFinite(autoFit?.maxLines)
    ? Math.max(1, Number(autoFit.maxLines))
    : derivedMaxLinesByHeight;
  const minFontSizeForFit = Number.isFinite(autoFit?.minFontSize)
    ? Math.max(4, Number(autoFit.minFontSize))
    : 10;
  const maxFontSizeForFit = Number.isFinite(autoFit?.maxFontSize)
    ? Math.max(minFontSizeForFit, Math.min(Number(autoFit.maxFontSize), baseFontSize))
    : baseFontSize;

  const fitted = (() => {
    if (overflow === "auto-scale") {
      return autoFitTextToBox({
        text: t.text,
        boxW,
        boxH,
        fontFamily,
        fontWeight: t.fontWeight,
        fontStyle: t.fontStyle,
        lineHeight,
        safePadding: textPadding,
        maxLines: Number.isFinite(autoFit?.maxLines) ? Math.max(1, Number(autoFit.maxLines)) : Infinity,
        minFontSize: minFontSizeForFit,
        maxFontSize: baseFontSize,
      });
    }

    if (mode === "shrink-to-fit") {
      return autoFitTextToBox({
        text: t.text,
        boxW,
        boxH,
        fontFamily,
        fontWeight: t.fontWeight,
        fontStyle: t.fontStyle,
        lineHeight,
        safePadding: textPadding,
        maxLines: overflow === "ellipsis"
          ? maxLinesForEllipsis
          : (Number.isFinite(autoFit?.maxLines) ? Math.max(1, Number(autoFit.maxLines)) : Infinity),
        minFontSize: minFontSizeForFit,
        maxFontSize: maxFontSizeForFit,
      });
    }

    if (mode === "multiline-wrap" || mode === "expand-box") {
      if (overflow === "ellipsis") {
        return autoFitTextToBox({
          text: t.text,
          boxW,
          boxH,
          fontFamily,
          fontWeight: t.fontWeight,
          fontStyle: t.fontStyle,
          lineHeight,
          safePadding: textPadding,
          maxLines: maxLinesForEllipsis,
          minFontSize: baseFontSize,
          maxFontSize: baseFontSize,
        });
      }
      return { text: t.text, fontSize: baseFontSize };
    }

    // mode === "none"
    if (overflow === "ellipsis") {
      const maxLines = Number.isFinite(autoFit?.maxLines) ? Math.max(1, Number(autoFit.maxLines)) : 1;
      return autoFitTextToBox({
        text: t.text,
        boxW,
        boxH,
        fontFamily,
        fontWeight: t.fontWeight,
        fontStyle: t.fontStyle,
        lineHeight,
        safePadding: textPadding,
        maxLines,
        minFontSize: baseFontSize,
        maxFontSize: baseFontSize,
      });
    }

    return { text: t.text, fontSize: baseFontSize };
  })();

  // ============ TEXT ANIMATION (textReveal) ============
  // textReveal is interpolated from keyframes at current playhead time
  const textReveal = (t as any).textReveal as number | undefined;
  const textAnimPreset = (t as any).textAnimationPreset as string | undefined;
  const textAnimDirection = ((t as any).textAnimationDirection || "forward") as TextAnimationDirection;

  // Apply text reveal animation if present
  const displayText = (textReveal !== undefined && textReveal < 1 && textAnimPreset)
    ? computeVisibleText(String(fitted.text ?? ""), textReveal, textAnimPreset, textAnimDirection)
    : String(fitted.text ?? "");

  // Apply textTransform to display text
  const textTransform = (t as any).textTransform as string | undefined;
  const transformedText = (() => {
    if (!textTransform || textTransform === "none") return displayText;
    if (textTransform === "uppercase") return displayText.toUpperCase();
    if (textTransform === "lowercase") return displayText.toLowerCase();
    if (textTransform === "capitalize")
      return displayText.replace(/\b\w/g, (c) => c.toUpperCase());
    return displayText;
  })();

  // Text warp
  const textWarp = (t as any).textWarp as { type: string; amount: number; offsetX?: number; offsetY?: number } | undefined;
  const hasWarp = textWarp && textWarp.type && textWarp.type !== "none";

  const wrapMode =
    mode === "multiline-wrap" || mode === "expand-box"
      ? (overflow === "ellipsis" ? "none" : "word")
      : "none";

  const readability = (t as any)?.readability as any | undefined;
  const overlayType = String(readability?.overlay ?? "off");
  const overlayStrength = Math.max(0, Math.min(1, Number(readability?.strength ?? 0.35)));
  const overlayPadding = Math.max(0, Number(readability?.padding ?? 0) || 0);
  const overlayCornerRadius = Math.max(0, Number(readability?.cornerRadius ?? 12) || 0);
  const overlayW = Math.max(1, boxW - overlayPadding * 2);
  const overlayH = Math.max(1, boxH - overlayPadding * 2);
  const overlayBlur = overlayType === "glass" ? Math.max(0, Number(readability?.blur ?? 8)) : 0;

  const overlayNode = (() => {
    if (overlayType === "off") return null;

    const customOverlayColor = (t as any)?.readability?.color as string | undefined;
    const baseRgb = customOverlayColor
      ? (parseRgbLike(customOverlayColor) ?? { r: 0, g: 0, b: 0 })
      : (parseRgbLike(String((t as any)?.fill ?? "#ffffff")) ?? { r: 255, g: 255, b: 255 });
    const lum = customOverlayColor ? 0 : relativeLuminance(baseRgb);
    const plateRgb = customOverlayColor ? baseRgb : (lum > 0.6 ? { r: 0, g: 0, b: 0 } : { r: 255, g: 255, b: 255 });
    const alpha = Math.max(0, Math.min(1, 0.7 * overlayStrength));

    if (overlayType === "gradient") {
      return (
        <Rect
          x={overlayPadding}
          y={overlayPadding}
          width={overlayW}
          height={overlayH}
          cornerRadius={overlayCornerRadius}
          fillLinearGradientStartPoint={{ x: 0, y: 0 }}
          fillLinearGradientEndPoint={{ x: 0, y: overlayH }}
          fillLinearGradientColorStops={[
            0,
            rgbaFromRgb(plateRgb, 0),
            1,
            rgbaFromRgb(plateRgb, alpha),
          ]}
          listening={false}
          perfectDrawEnabled={false}
        />
      );
    }

    if (overlayType === "glass") {
      const glassFill = rgbaFromRgb({ r: 255, g: 255, b: 255 }, Math.max(0.06, 0.18 * overlayStrength));
      const glassStroke = rgbaFromRgb({ r: 255, g: 255, b: 255 }, Math.max(0.12, 0.24 * overlayStrength));
      return (
        <Rect
          x={overlayPadding}
          y={overlayPadding}
          width={overlayW}
          height={overlayH}
          cornerRadius={overlayCornerRadius}
          fill={glassFill}
          stroke={glassStroke}
          strokeWidth={1}
          filters={overlayBlur > 0 ? [KonvaLib.Filters.Blur] : undefined}
          blurRadius={overlayBlur}
          listening={false}
          perfectDrawEnabled={false}
        />
      );
    }

    // backplate (default)
    return (
      <Rect
        x={overlayPadding}
        y={overlayPadding}
        width={overlayW}
        height={overlayH}
        cornerRadius={overlayCornerRadius}
        fill={rgbaFromRgb(plateRgb, alpha)}
        listening={false}
        perfectDrawEnabled={false}
      />
    );
  })();

  const textOpacity = el.id === editingTextId ? 0 : (common.opacity ?? 1);

  return (
    <Group
      key={el.id}
      {...common}
      opacity={textOpacity}
      width={boxW}
      height={boxH}
      onDblClick={(e: any) => {
        e.cancelBubble = true;
        const node = e.target;
        const stage = node.getStage?.();
        if (!stage) return;

        const abs = node.getAbsoluteTransform().point({ x: 0, y: 0 });
        const absScale = node.getAbsoluteScale?.() || { x: stage.scaleX(), y: stage.scaleY() };
        const stageBox = stage.container().getBoundingClientRect();

        const screenX = stageBox.left + abs.x;
        const screenY = stageBox.top + abs.y;
        const screenWidth = Math.max(1, boxW * absScale.x);
        const screenHeight = Math.max(1, boxH * absScale.y);
        const fontSizePx = Math.max(1, fitted.fontSize * absScale.y);

        window.dispatchEvent(
          new CustomEvent("vb:edit-text", {
            detail: {
              elementId: el.id,
              element: t,
              position: { x: screenX, y: screenY, width: screenWidth, height: screenHeight, fontSizePx },
            },
          })
        );
      }}
      // Text transform persistence is handled by the generic onNodeTransformEnd
      // in useEditor2CanvasInteraction.ts (edge/corner aware font scaling).
    >
      {/* Hit area (bottom layer) - keep alpha >= ~1/255 so Konva hit graph can detect it */}
      <Rect
        x={0}
        y={0}
        width={boxW}
        height={boxH}
        fill="rgba(0,0,0,0.01)"
        listening={true}
        perfectDrawEnabled={false}
      />
      {/* Clipped Group for text overflow - visual only */}
      <Group
        clipX={0}
        clipY={0}
        clipWidth={boxW}
        clipHeight={boxH}
        listening={false}
      >
        {overlayNode}
        {hasWarp ? (
          <Shape
            x={textWarp?.offsetX ?? 0}
            y={textWarp?.offsetY ?? 0}
            width={boxW}
            height={boxH}
            sceneFunc={(konvaCtx) => {
              const ctx = (konvaCtx as any)._context as CanvasRenderingContext2D;
              if (!ctx) return;
              drawWarpedText(ctx, {
                text: transformedText,
                width: boxW,
                height: boxH,
                fontSize: Math.max(1, Number(fitted.fontSize) || baseFontSize),
                fontFamily,
                fontStyleStr: fontStyle,
                fill: (fillProps as any).fill ?? t.fill ?? "#ffffff",
                letterSpacing: t.letterSpacing || 0,
                warpType: (textWarp?.type || "none") as WarpType,
                warpAmount: textWarp?.amount ?? 50,
                strokeColor: strokeColor || undefined,
                strokeWidth: strokeWidth || undefined,
                shadowBlur: shadowEnabled ? shadowBlur : undefined,
                shadowColor: shadowEnabled ? shadowColor : undefined,
                shadowOffsetX: shadowEnabled ? shadowOffsetX : undefined,
                shadowOffsetY: shadowEnabled ? shadowOffsetY : undefined,
              });
            }}
            listening={false}
          />
        ) : (
          <KText
            x={0}
            y={0}
            width={boxW}
            height={boxH}
            text={transformedText}
            fontSize={Math.max(1, Number(fitted.fontSize) || baseFontSize)}
            fontFamily={fontFamily}
            fontStyle={fontStyle}
            {...fillProps}
            align={t.align}
            verticalAlign={verticalAlign as any}
            padding={textPadding}
            wrap={wrapMode as any}
            letterSpacing={t.letterSpacing || 0}
            lineHeight={lineHeight}
            textDecoration={t.textDecoration && t.textDecoration !== "none" ? t.textDecoration : ""}
            shadowBlur={shadowBlur}
            shadowOffsetX={shadowOffsetX}
            shadowOffsetY={shadowOffsetY}
            shadowColor={shadowColor}
            shadowEnabled={shadowEnabled}
            shadowOpacity={shadowOpacity}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            listening={false}
          />
        )}
      </Group>
    </Group>
  );
}
